import * as T from 'three';
import {mergeGeometries} from 'three/addons/utils/BufferGeometryUtils.js';

const clamp=(n,a,b)=>Math.min(b,Math.max(a,n));
const ease=t=>{const q=clamp(t,0,1);return q*q*(3-2*q);};
const startZ=8,finishZ=3985,splitStart=2850,splitEnd=3540;
const terrainBounds={minX:-320,maxX:320,minZ:-96,maxZ:4096};
const bypassStart=3030,bypassEnd=3520;
const waves=[[80,780,105],[780,1450,-90],[1450,2180,112],[2180,2850,-90]];
const routeId=route=>route==='pines'||route==='right'?'pines':'valley';
const commonWidths=[[8,10],[70,12]];
for(const [a,b]of waves){const length=b-a;for(const [t,w]of [[0,12],[.2,14],[.5,12],[.8,14],[1,12]])commonWidths.push([a+length*t,w]);}
const sharedWidths=[...new Map(commonWidths.map(knot=>[knot[0],knot])).values()];
const widths={
  valley:[...sharedWidths,[2970,12],[3120,13],[3200,14],[3350,12],[3480,14],[3540,14],[3730,12],[3985,14]],
  pines:[...sharedWidths,[2970,10],[3050,9],[3120,12],[3220,12],[3350,11],[3450,14],[3540,14],[3730,12],[3985,14]],
};
function halfWidthAt(id,z){
  const knots=widths[id];let i=0;while(i<knots.length-2&&z>knots[i+1][0])i++;
  const a=knots[i],b=knots[i+1];return a[1]+(b[1]-a[1])*ease((z-a[0])/(b[0]-a[0]));
}
// Position, tangent and curvature are continuous at the fork and merge. The
// road has no hidden Catmull-Rom corner when it becomes too narrow to cut across.
function pathShape(id,z){
  if(z<=80||z>=finishZ||z===splitStart||z===splitEnd)return {x:0,dx:0,ddx:0};
  if(z<splitStart){
    const [begin,end,amplitude]=waves.find(([,end])=>z<end),length=end-begin,t=(z-begin)/length,a=2*Math.PI*t;
    return {x:amplitude*(.5*Math.sin(a)-.25*Math.sin(2*a)),dx:amplitude*Math.PI/length*(Math.cos(a)-Math.cos(2*a)),ddx:amplitude*Math.PI**2/length**2*(-2*Math.sin(a)+4*Math.sin(2*a))};
  }
  if(z<splitEnd){
    const length=splitEnd-splitStart,t=(z-splitStart)/length,u=1-t;
    const e=64*t**3*u**3,d=192*t*t*u*u*(1-2*t),dd=384*t*u*(1-5*t+5*t*t);
    const a=id==='pines'?-136+4*Math.sin(4*Math.PI*t):145,da=id==='pines'?16*Math.PI*Math.cos(4*Math.PI*t):0,dda=id==='pines'?-64*Math.PI**2*Math.sin(4*Math.PI*t):0;
    return {x:e*a,dx:(d*a+e*da)/length,ddx:(dd*a+2*d*da+e*dda)/length**2};
  }
  const length=finishZ-splitEnd,t=(z-splitEnd)/length,s=Math.sin(Math.PI*t),c=Math.cos(Math.PI*t);
  return {x:28*s**4,dx:112*Math.PI*s**3*c/length,ddx:112*Math.PI**2*(3*s*s*c*c-s**4)/length**2};
}
function bypassShape(z){
  const p=pathShape('pines',z),length=bypassEnd-bypassStart,t=clamp((z-bypassStart)/length,0,1),u=1-t;
  return {x:p.x-40*64*t**3*u**3,dx:p.dx-40*192*t*t*u*u*(1-2*t)/length,ddx:p.ddx-40*384*t*u*(1-5*t+5*t*t)/length**2};
}
export function routeCenter(route,z){
  const id=routeId(route),station=clamp(z,startZ,finishZ),p=pathShape(id,station);
  return {route:id,x:p.x,z:station,heading:Math.atan2(p.dx,1),halfWidth:halfWidthAt(id,station)};
}
// Three bounded Newton steps solve the local perpendicular projection; no
// terrain/board/hand query scans the hundreds of navigation polyline samples.
function projectRoad(id,x,z,bypass=false){
  const shape=bypass?bypassShape:t=>pathShape(id,t);let station=clamp(z,terrainBounds.minZ,terrainBounds.maxZ);
  for(let i=0;i<3;i++){
    const p=shape(station),denominator=Math.max(.35,1+p.dx*p.dx-(x-p.x)*p.ddx);
    const step=clamp(((x-p.x)*p.dx+z-station)/denominator,-32,32);
    station=clamp(station+step,terrainBounds.minZ,terrainBounds.maxZ);
    if(Math.abs(step)<.0001)break;
  }
  const p=shape(station),scale=Math.hypot(p.dx,1),signed=((x-p.x)-(z-station)*p.dx)/scale;
  const baseWidth=halfWidthAt(id,station),t=clamp((station-bypassStart)/(bypassEnd-bypassStart),0,1);
  const halfWidth=bypass?baseWidth+(8-baseWidth)*64*t**3*(1-t)**3:baseWidth;
  return {route:id,x:p.x,z:station,heading:Math.atan2(p.dx,1),halfWidth,signed,offset:Math.abs(signed),outside:Math.abs(signed)-halfWidth,bypass};
}
function roadCandidates(x,z){
  return [projectRoad('valley',x,z),projectRoad('pines',x,z),projectRoad('pines',x,z,true)];
}
export function corridorAt(x,z){return roadCandidates(x,z).reduce((a,b)=>a.outside<=b.outside?a:b);}

// Integrate grade against route distance, rather than the global +Z direction.
// Projecting back to this height curve makes gravity follow each mountain road.
const grades=[[-200,.21],[65,.21],[105,.20],[170,.17],[390,.155],[560,.18],[780,.15],[1000,.175],[1240,.16],[1450,.17],[1640,.15],[1900,.18],[2180,.15],[2420,.175],[2680,.15],[2850,.135],[3030,.13],[3220,.135],[3410,.12],[3540,.13],[3670,.14],[3810,.10],[3910,.055],[3990,0],[4200,0]];
function gradeAt(z){
  let i=0;while(i<grades.length-2&&z>grades[i+1][0])i++;
  const a=grades[i],b=grades[i+1];return a[1]+(b[1]-a[1])*ease((z-a[0])/(b[0]-a[0]));
}
function verticalGrade(z){
  const a=pathShape('valley',z).dx,b=pathShape('pines',z).dx;
  return gradeAt(z)*(Math.hypot(a,1)+Math.hypot(b,1))*.5;
}
const heightMin=-200,heightMax=4200,heightValues=new Float64Array(heightMax-heightMin+1),heightSlopes=new Float64Array(heightValues.length);
for(let i=0;i<heightValues.length;i++){
  const z=heightMin+i;heightSlopes[i]=-verticalGrade(z);
  if(i)heightValues[i]=heightValues[i-1]-(verticalGrade(z-1)+4*verticalGrade(z-.5)+verticalGrade(z))/6;
}
const heightOrigin=heightValues[startZ-heightMin];
function downhillHeight(z){
  const station=clamp(z,heightMin,heightMax),i=Math.min(heightValues.length-2,Math.floor(station-heightMin)),t=station-heightMin-i;
  return (2*t**3-3*t*t+1)*heightValues[i]+(t**3-2*t*t+t)*heightSlopes[i]+(-2*t**3+3*t*t)*heightValues[i+1]+(t**3-t*t)*heightSlopes[i+1]-heightOrigin;
}
export {downhillHeight as alpineBaseHeight};
function roadHeight(p,x,z){
  const across=Math.abs(p.signed),outside=Math.max(0,across-p.halfWidth);
  const crown=-.035*Math.min(1,(across/p.halfWidth)**2);
  const shoulder=.32*ease(outside/1.8)*(1-ease((outside-1.8)/4.8));
  // The uphill side rises into a snow-covered cut; the valley side has a broad
  // forgiving shoulder before falling away. Neither is a vertical collider wall.
  const hillside=p.signed<0?12*ease((outside-2.5)/24)+18*ease((outside-26.5)/58):-8*ease((outside-3.5)/32)-17*ease((outside-35.5)/82);
  const irregular=(Math.sin(p.z/33)*.55+Math.sin(p.z/71+.7)*.7)*ease(outside/15);
  const separation=Math.abs(pathShape('valley',z).x-pathShape('pines',z).x);
  const splitBlend=z>splitStart&&z<splitEnd?ease((separation-18)/34):1;
  const station=p.bypass?projectRoad('pines',x,z).z:p.z;
  const base=downhillHeight(z)+(downhillHeight(station)-downhillHeight(z))*splitBlend;
  return base+crown+shoulder+hillside+irregular;
}
export function alpineGround(x,z){
  const candidates=roadCandidates(x,z),outside=Math.min(...candidates.map(p=>p.outside));
  // Blend only where routes share a shoulder or in the remote divide. A hard
  // nearest-branch switch would otherwise create a ridge seam between slopes.
  const blend=4+Math.min(38,Math.max(0,outside)*1.5);let height=0,weight=0;
  const interior=ease(-outside/2);
  for(const p of candidates){const w=(1-ease((p.outside-outside)/blend))*(1-interior+interior*ease(-p.outside/1.5));height+=roadHeight(p,x,z)*w;weight+=w;}
  const remote=ease((outside-30)/45),mountain=downhillHeight(z)-x*.16+1.6*Math.sin(z/120+x/100);
  return height/weight*(1-remote)+mountain*remote;
}
export function roadCrossSection(route,z,offset){
  const p=routeCenter(route,z),x=p.x+Math.cos(p.heading)*offset,station=p.z-Math.sin(p.heading)*offset;
  return {x,z:station,y:alpineGround(x,station),offset,halfWidth:p.halfWidth,heading:p.heading};
}

function routeSamples(id){
  const result=[];let distance=0;
  for(let z=startZ;z<=finishZ+1;z+=2){
    z=Math.min(z,finishZ);const p=routeCenter(id,z);p.y=alpineGround(p.x,p.z);
    if(result.length){const last=result.at(-1);distance+=Math.hypot(p.x-last.x,p.z-last.z);}
    result.push({...p,distance});if(z===finishZ)break;
  }
  return result;
}
const sampledRoutes={valley:routeSamples('valley'),pines:routeSamples('pines')};
export function routePolyline(route){return sampledRoutes[routeId(route)];}
export function routeProgress(route,x,z){
  const id=routeId(route),samples=sampledRoutes[id];let best=null;
  const projected=projectRoad(id,x,z),middle=clamp(Math.floor((projected.z-startZ)/2),0,samples.length-2),begin=Math.max(0,middle-36),end=Math.min(samples.length-1,middle+37);
  for(let i=begin;i<end;i++){
    const a=samples[i],b=samples[i+1],dx=b.x-a.x,dz=b.z-a.z,t=clamp(((x-a.x)*dx+(z-a.z)*dz)/(dx*dx+dz*dz),0,1);
    const px=a.x+dx*t,pz=a.z+dz*t,offset=Math.hypot(x-px,z-pz);if(best&&offset>=best.offset)continue;
    const distance=a.distance+(b.distance-a.distance)*t;
    best={route:id,x:px,z:pz,heading:Math.atan2(dx,dz),halfWidth:a.halfWidth+(b.halfWidth-a.halfWidth)*t,offset,distance};
  }
  const total=samples.at(-1).distance;
  return {...best,y:alpineGround(best.x,best.z),total,remaining:Math.max(0,total-best.distance),progress:best.distance/total};
}
export function nearestSafePoint(x,z,route){
  const left=routeProgress('valley',x,z),right=routeProgress('pines',x,z),closest=route?routeProgress(route,x,z):(left.offset<=right.offset?left:right);
  const point=routeCenter(closest.route,clamp(closest.z-5,startZ,finishZ-8));return {...point,y:alpineGround(point.x,point.z)};
}
const bypassPoints=Array.from({length:25},(_,i)=>{const z=bypassStart+(bypassEnd-bypassStart)*i/24;return {x:bypassShape(z).x,z};});
const challengeExit={x:(pathShape('pines',3480).x+pathShape('pines',3515).x)/2,z:3480,halfWidth:7.5,length:35,maxSpeed:12/3.6};
const turnCues=waves.flatMap(([a,b,amplitude],i)=>[.11,.36,.64,.89].map((t,j)=>{const left=(j%2===0)===(amplitude>0);return {id:`ridge-${i+1}-turn-${j+1}`,atZ:a+(b-a)*t,leadMeters:i===0&&j===0?75:145,text:`${left?'左':'右'}长弯 · 提前留出入弯空间`,kind:'turn',direction:left?'left':'right'};}));
export const ALPINE_REGION={
  start:{x:0,z:startZ,heading:0},fork:{x:0,z:splitStart},merge:{x:0,z:splitEnd},finish:{x:0,z:finishZ,radius:14},
  bounds:{minX:-250,maxX:250,minZ:-25,maxZ:4040},terrainBounds,split:{startZ:splitStart,endZ:splitEnd},
  sections:[
    {id:'departure',name:'木屋顺坡',startZ:8,endZ:80},
    {id:'ridge-one',name:'山顶环山弯',startZ:80,endZ:780},
    {id:'ridge-two',name:'雪脊长弯',startZ:780,endZ:1450},
    {id:'ridge-three',name:'谷壁连弯',startZ:1450,endZ:2180},
    {id:'ridge-four',name:'林缘起伏',startZ:2180,endZ:2800},
    {id:'forest-entry',name:'松林分路',startZ:2800,endZ:3030},
    {id:'branches',name:'山谷与松林',startZ:3030,endZ:3540},
    {id:'arrival',name:'观景缓坡',startZ:3540,endZ:3985},
  ],
  cues:[
    ...turnCues,
    {id:'forest',atZ:2800,leadMeters:150,text:'前方林间 · 留意分路',kind:'slow'},
    {id:'fork',atZ:2850,leadMeters:170,text:'左行山谷 · 右行松林',kind:'fork'},
    {id:'challenge',atZ:3030,leadMeters:150,text:'林间三弯 · 可选挑战',kind:'challenge',route:'pines',challengeState:['idle','skipped']},
    {id:'valley-turn',atZ:3190,leadMeters:145,text:'山谷右弯 · 顺着软雪肩',kind:'turn',direction:'right',route:'valley'},
    {id:'pines-turn',atZ:3080,leadMeters:145,text:'松林左弯 · 留好入弯空间',kind:'turn',direction:'left',route:'pines'},
    {id:'challenge-exit',atZ:3480,leadMeters:160,text:'出口浅绿雪区 · 减速至12 km/h以下',kind:'challenge',route:'pines',challengeState:'active'},
    {id:'merge',atZ:3540,leadMeters:145,text:'前方两线汇合',kind:'merge'},
    {id:'arrival',atZ:3985,leadMeters:150,text:'观景台 · 慢慢收住',kind:'finish'},
  ],
  routes:{valley:{id:'valley',name:'山谷线',distance:sampledRoutes.valley.at(-1).distance},pines:{id:'pines',name:'松林线',distance:sampledRoutes.pines.at(-1).distance}},
  challenge:{
    id:'three-turns',name:'林间三弯',entrance:{x:pathShape('pines',3030).x,z:3030,radius:12},retry:{...routeCenter('pines',3005)},
    gates:[3125,3220,3315].map((z,i)=>({x:pathShape('pines',z).x+(i===1?4:-4),z,halfWidth:5})),
    exit:challengeExit,bypass:bypassPoints,
  },
};

const palettes={
  day:{skyTop:'#246fa8',skyBottom:'#88b9d6',fog:'#b2ccdf',fogNear:310,fogFar:1550,sun:'#fff0d7',sunPower:2.3,skyLight:'#dfecf5',groundLight:'#8aa6bf',ambient:1.7,light:[-34,54,-28],mountain:'#ffffff',sign:'#ffffff',flakes:'#f1f7fc'},
  sunset:{skyTop:'#7599bc',skyBottom:'#f0ba94',fog:'#c7b7be',fogNear:260,fogFar:1450,sun:'#ffd09a',sunPower:1.75,skyLight:'#cad6e7',groundLight:'#8291ae',ambient:1.55,light:[-36,24,85],mountain:'#eccbd0',sign:'#ffe5c6',flakes:'#f8dfcd'},
  night:{skyTop:'#061525',skyBottom:'#23435f',fog:'#294963',fogNear:170,fogFar:950,sun:'#a5caff',sunPower:.72,skyLight:'#7597c3',groundLight:'#314e70',ambient:.79,light:[-40,58,48],mountain:'#4d6f99',sign:'#9bb9d9',flakes:'#a9c4e4'},
};
const cloudPalettes={
  day:{skyTop:'#8babbf',skyBottom:'#becfd9',fog:'#aebfcc',fogNear:75,fogFar:390,ambient:1.75},
  sunset:{skyTop:'#9a9fb6',skyBottom:'#e4baa4',fog:'#b9abb8',fogNear:90,fogFar:490,ambient:1.58},
  night:{skyTop:'#14283d',skyBottom:'#304b63',fog:'#314c64',fogNear:55,fogFar:325,ambient:.84},
};

function randomSeed(seed){return()=>{seed=(Math.imul(seed,1664525)+1013904223)>>>0;return seed/4294967296;};}

function pineGeometry(){
  const positions=[],colors=[],rng=randomSeed(7214);
  const green=new T.Color('#2e5d5e'),dark=new T.Color('#224b53'),snow=new T.Color('#dcece7');
  function tri(a,b,c,color){for(const p of [a,b,c]){positions.push(...p);colors.push(color.r,color.g,color.b);}}
  // Seven asymmetric branch whorls leave gaps and a visible trunk; the silhouette
  // remains a conifer without stacking identical, solid cones.
  for(let level=0;level<7;level++){
    const y=.7+level*.72,size=1.75*(1-level/8);
    for(let branch=0;branch<7;branch++){
      const a=branch/7*Math.PI*2+level*.49,len=size*(.78+rng()*.34);
      const p=(r,angle,h)=>[Math.cos(angle)*r,h,Math.sin(angle)*r];
      const root=p(.08,a,y+.48),tip=p(len,a,y-.15),left=p(len*.62,a-.42,y),right=p(len*.67,a+.35,y),crest=p(len*.43,a,y+.24),bottom=p(len*.45,a,y-.2);
      tri(root,left,crest,green);tri(root,crest,right,green);tri(left,tip,crest,snow);tri(tip,right,crest,snow);tri(left,bottom,tip,dark);tri(tip,bottom,right,dark);tri(root,bottom,left,dark);tri(root,right,bottom,dark);
    }
  }
  const geometry=new T.BufferGeometry();geometry.setAttribute('position',new T.Float32BufferAttribute(positions,3));geometry.setAttribute('color',new T.Float32BufferAttribute(colors,3));geometry.computeVertexNormals();return geometry;
}

export function createAlpineRegion(scene){
  const root=new T.Group();root.name='alpine-adventure-region';scene.add(root);
  // Preserve authored scenery placements independently of material generation.
  const rng=randomSeed(2690623532),obstacles=[],textures=[],gateObjects=[],mountainMaterials=[],signMaterials=[];
  const previousFog=scene.fog;
  const materials=new Map();
  function material(color,extra={}){const key=color+JSON.stringify(extra);if(!materials.has(key))materials.set(key,new T.MeshStandardMaterial({color,roughness:.88,...extra}));return materials.get(key);}
  function box(parent,size,position,color,extra={}){const mesh=new T.Mesh(new T.BoxGeometry(...size),material(color,extra));mesh.position.set(...position);mesh.castShadow=true;mesh.receiveShadow=true;parent.add(mesh);return mesh;}
  function cylinder(parent,radius,height,position,color){const mesh=new T.Mesh(new T.CylinderGeometry(radius,radius*1.06,height,8),material(color));mesh.position.set(...position);mesh.castShadow=true;mesh.receiveShadow=true;parent.add(mesh);return mesh;}
  function beam(parent,a,b,radius,color){const start=new T.Vector3(...a),end=new T.Vector3(...b),mesh=cylinder(parent,radius,start.distanceTo(end),start.clone().add(end).multiplyScalar(.5).toArray(),color);mesh.quaternion.setFromUnitVectors(new T.Vector3(0,1,0),end.sub(start).normalize());return mesh;}

  // Keep one-metre snow under the road, with coarse mountain cells outside it.
  // Only shoulder/intersection errors refine further. Neighboring cell edges
  // share every subdivision point, including across 128-metre terrain sections.
  const terrainSections=[],snowColor=new T.Color('#f0f1ee'),bankColor=new T.Color('#b3c4d2');
  const {minX,maxX,minZ:terrainMin,maxZ:terrainMax}=terrainBounds,nx=maxX-minX;
  const levels=new Int8Array(nx*(terrainMax-terrainMin));levels.fill(-3);
  const sections=new Map(),cacheStride=(maxX-minX+4)*8;
  let heightCache=new Map();
  function heightKey(x,z){return Math.round((x-minX+2)*8)+Math.round((z-terrainMin+2)*8)*cacheStride;}
  function sampleHeight(x,z){const key=heightKey(x,z);let h=heightCache.get(key);if(h===undefined){h=alpineGround(x,z);heightCache.set(key,h);}return h;}
  function levelAt(x,z){const ix=Math.floor(x)-minX,iz=Math.floor(z)-terrainMin;return ix<0||ix>=nx||iz<0||iz>=terrainMax-terrainMin?-3:levels[iz*nx+ix];}
  function classify(x,z,size,cells){
    const outside=corridorAt(x+size*.5,z+size*.5).outside;
    if(size>1&&outside<size*.75+3){const h=size*.5;classify(x,z,h,cells);classify(x+h,z,h,cells);classify(x,z+h,h,cells);classify(x+h,z+h,h,cells);return;}
    let subdivision=1;
    if(size===1&&(outside> -2.5||z>splitStart&&z<splitEnd)&&outside<2.5){
      const corners=[sampleHeight(x,z),sampleHeight(x+1,z),sampleHeight(x,z+1),sampleHeight(x+1,z+1)];let error=0;
      for(const [u,v]of [[.5,0],[.5,1],[0,.5],[1,.5],[.25,.25],[.75,.25],[.25,.75],[.75,.75],[.5,.5]]){
        const qx=x+u,qz=z+v;
        if(corridorAt(qx,qz).outside>.75)continue;
        const mesh=u+v<=1?corners[0]+(corners[1]-corners[0])*u+(corners[2]-corners[0])*v:corners[3]+(corners[2]-corners[3])*(1-u)+(corners[1]-corners[3])*(1-v);
        error=Math.max(error,Math.abs(mesh-sampleHeight(qx,qz)));
      }
      subdivision=error>.014?8:error>.006?4:error>.003?2:1;
    }
    const level=Math.log2(subdivision/size);
    for(let row=z;row<z+size;row++)levels.fill(level,(row-terrainMin)*nx+x-minX,(row-terrainMin)*nx+x-minX+size);
    cells.push({x,z,size,subdivision});
  }
  for(let start=terrainMin;start<terrainMax;start+=128){
    const cells=[];heightCache.clear();
    for(let z=start;z<Math.min(start+128,terrainMax);z+=8)for(let x=minX;x<maxX;x+=8)classify(x,z,8,cells);
    sections.set(start,cells);
  }
  for(const [start,cells]of sections){
    heightCache.clear();const positions=[],colors=[],uvs=[],normals=[],indices=[],vertices=new Map();let refinedCells=0,transitionCells=0;
    function vertex(x,z){
      const key=heightKey(x,z);if(vertices.has(key))return vertices.get(key);
      const index=positions.length/3,y=sampleHeight(x,z),outside=corridorAt(x,z).outside;vertices.set(key,index);
      positions.push(x,y,z);uvs.push(x/16,z/16);
      const across=sampleHeight(x+.5,z)-sampleHeight(x-.5,z),along=sampleHeight(x,z+.5)-sampleHeight(x,z-.5),length=Math.hypot(across,1,along);
      normals.push(-across/length,1/length,-along/length);
      const wind=.5+.5*Math.sin(x*.065+z*.023+Math.sin(z*.009)*1.7),shoulder=.22*ease(outside/7)*(1-ease((outside-14)/24));
      const blue=clamp(ease((outside+3)/38)*.31+clamp(across*.5-along*.12,0,.17)+wind*.055+shoulder,0,.65);
      colors.push(snowColor.r+(bankColor.r-snowColor.r)*blue,snowColor.g+(bankColor.g-snowColor.g)*blue,snowColor.b+(bankColor.b-snowColor.b)*blue);return index;
    }
    function sideVertices(ax,az,bx,bz,size){
      const result=[],dx=(bx-ax)/size,dz=(bz-az)/size;
      const outsideX=-dz*.001,outsideZ=dx*.001;
      if(size<=1){
        const adjacent=2**-levelAt((ax+bx)*.5+outsideX,(az+bz)*.5+outsideZ),n=Math.max(1,Math.round(size/adjacent));
        for(let i=0;i<n;i++)result.push(vertex(ax+dx*size*i/n,az+dz*size*i/n));
      }else{
        for(let unit=0;unit<size;unit++){
          const adjacent=2**-levelAt(ax+dx*(unit+.5)+outsideX,az+dz*(unit+.5)+outsideZ);
          if(adjacent>=1){const coordinate=dx?ax+dx*unit:az+dz*unit;if(unit===0||Math.abs(coordinate/adjacent-Math.round(coordinate/adjacent))<.00001)result.push(vertex(ax+dx*unit,az+dz*unit));}
          else for(let j=0;j<1/adjacent;j++)result.push(vertex(ax+dx*(unit+j*adjacent),az+dz*(unit+j*adjacent)));
        }
      }
      return result;
    }
    for(const {x,z,size,subdivision:n}of cells){
      if(n>1)refinedCells++;const step=size/n;
      for(let row=0;row<n;row++)for(let column=0;column<n;column++){
        const x0=x+column*step,x1=x0+step,z0=z+row*step,z1=z0+step;
        const ring=[...sideVertices(x0,z0,x0,z1,step),...sideVertices(x0,z1,x1,z1,step),...sideVertices(x1,z1,x1,z0,step),...sideVertices(x1,z0,x0,z0,step)];
        if(ring.length===4)indices.push(ring[0],ring[1],ring[3],ring[3],ring[1],ring[2]);
        else{transitionCells++;const center=vertex((x0+x1)*.5,(z0+z1)*.5);for(let i=0;i<ring.length;i++)indices.push(center,ring[i],ring[(i+1)%ring.length]);}
      }
    }
    const geometry=new T.BufferGeometry();geometry.setAttribute('position',new T.Float32BufferAttribute(positions,3));geometry.setAttribute('color',new T.Float32BufferAttribute(colors,3));geometry.setAttribute('uv',new T.Float32BufferAttribute(uvs,2));geometry.setAttribute('normal',new T.Float32BufferAttribute(normals,3));geometry.setIndex(indices);geometry.computeBoundingSphere();
    geometry.userData.refinement={minimumSpacing:.125,maximumSpacing:8,refinedCells,transitionCells,startZ:start,endZ:Math.min(start+128,terrainMax)};terrainSections.push({geometry,start});
  }
  heightCache.clear();sections.clear();
  // Powder has irregular, overlapping soft ripples rather than continuous
  // corduroy. Both tiny precomputed maps remain fixed in 16 m world-space tiles.
  const textureRng=randomSeed(6237),tau=Math.PI*2;
  function snowHeight(u,v,forNormal=false){
    return Math.sin(tau*(u*2+v)+.7*Math.sin(tau*v))*(forNormal?.08:.027)
      +Math.cos(tau*(u-v*3)+.45*Math.sin(tau*u))*(forNormal?.04:.017)
      +Math.sin(tau*(u*5+v*4)+.6*Math.cos(tau*(u-v)))*(forNormal?.014:.008)
      +Math.cos(tau*(u*11-v*7))*.0025
      +(forNormal?Math.sin(tau*(u*17+v*11)+2.1*Math.sin(tau*(u*2-v)))*.0017+Math.cos(tau*(u*9-v*21)+1.7*Math.cos(tau*(u+v*2)))*.0011:0);
  }
  function snowTexture(data,size,color=false){
    const texture=new T.DataTexture(data,size,size,T.RGBAFormat);texture.wrapS=texture.wrapT=T.RepeatWrapping;texture.magFilter=T.LinearFilter;texture.minFilter=T.LinearMipmapLinearFilter;texture.generateMipmaps=true;texture.anisotropy=4;if(color)texture.colorSpace=T.SRGBColorSpace;texture.needsUpdate=true;textures.push(texture);return texture;
  }
  const normalSize=256,normalData=new Uint8Array(normalSize*normalSize*4),heights=new Float32Array(normalSize*normalSize);
  for(let y=0;y<normalSize;y++)for(let x=0;x<normalSize;x++)heights[y*normalSize+x]=snowHeight(x/normalSize,y/normalSize,true);
  for(let y=0;y<normalSize;y++)for(let x=0;x<normalSize;x++){
    const at=(a,b)=>heights[((b+normalSize)%normalSize)*normalSize+(a+normalSize)%normalSize];
    const dx=(at(x+1,y)-at(x-1,y))/(32/normalSize),dz=(at(x,y+1)-at(x,y-1))/(32/normalSize),length=Math.hypot(dx,dz,1),i=(y*normalSize+x)*4;
    normalData[i]=Math.round((-.5*dx/length+.5)*255);normalData[i+1]=Math.round((-.5*dz/length+.5)*255);normalData[i+2]=Math.round((.5/length+.5)*255);normalData[i+3]=255;
  }
  const colorSize=512,colorData=new Uint8Array(colorSize*colorSize*4);
  for(let y=0;y<colorSize;y++)for(let x=0;x<colorSize;x++){
    const u=x/colorSize,v=y/colorSize,height=snowHeight(u,v);
    const value=250+height*36+(textureRng()-.5)*3,i=(y*colorSize+x)*4;
    colorData[i]=Math.round(value-1);colorData[i+1]=Math.round(value);colorData[i+2]=Math.round(value);colorData[i+3]=255;
  }
  const normalTexture=snowTexture(normalData,normalSize),snowColorTexture=snowTexture(colorData,colorSize,true);
  const groundMat=new T.MeshStandardMaterial({color:'#ffffff',vertexColors:true,roughness:.97,map:snowColorTexture,normalMap:normalTexture,normalScale:new T.Vector2(1.05,1.05)});
  for(const {geometry,start} of terrainSections){const terrain=new T.Mesh(geometry,groundMat);terrain.name=`powder-slope-${start}`;terrain.userData.terrain=true;terrain.receiveShadow=true;root.add(terrain);}

  // Place the finite forest budget around the road first. Short uneven groups
  // define its uphill edge; lower, widely spaced valley groups leave views open.
  const placements=[];
  function placeTree(q,scale,roadside=false){
    const outside=corridorAt(q.x,q.z).outside,radius=.55*scale,foliageRadius=2.05*scale;
    if(outside<Math.max(3.8,foliageRadius+.6)||placements.some(p=>Math.hypot(p.x-q.x,p.z-q.z)<1.6))return;
    placements.push({x:q.x,z:q.z,scale,rotation:rng()*Math.PI*2});
    if(outside<22)obstacles.push({type:'tree',x:q.x,z:q.z,radius,foliageRadius,roadside});
  }
  for(const route of ['valley','pines'])for(let station=48;station<finishZ-35;station+=12+rng()*8){
    if(route==='pines'&&(station<splitStart||station>splitEnd))continue;
    const count=rng()<.42?4:3;
    for(let i=0;i<count;i++){
      const z=station+(i-1)*3.0+(rng()-.5)*2.5,p=routeCenter(route,z),offset=p.halfWidth+(i===1?7.0+rng()*2.2:4.4+rng()*2.0);
      placeTree(roadCrossSection(route,z,-offset),1.05+rng()*.64,true);
    }
    if(rng()<.48)for(let i=0;i<2;i++){
      const z=station+5.5+i*3.2+rng()*2.5,p=routeCenter(route,z);
      placeTree(roadCrossSection(route,z,p.halfWidth+4.7+rng()*2.3),.90+rng()*.43,true);
    }
  }
  for(let attempt=0;attempt<10000&&placements.length<1650;attempt++){
    const z=-45+rng()*(finishZ+160),x=(rng()-.5)*460,corridor=corridorAt(x,z);
    if(corridor.outside<9)continue;
    if(z<45&&Math.hypot(x-16,z-23)<16||z>finishZ-30&&Math.abs(x)<38)continue;
    const pineDensity=z>splitStart-80&&z<splitEnd+30?(x<0?.97:.61):.49;if(rng()>pineDensity)continue;
    placeTree({x,z},.9+rng()*1.35);
  }
  const dummy=new T.Object3D(),forestChunks=new Map();
  for(const p of placements){const key=Math.floor(p.z/140);if(!forestChunks.has(key))forestChunks.set(key,[]);forestChunks.get(key).push(p);}
  const foliageGeo=pineGeometry(),foliageMat=new T.MeshStandardMaterial({vertexColors:true,roughness:1,flatShading:true}),trunkGeo=new T.CylinderGeometry(.09,.15,2.3,7);
  for(const [chunk,grove] of forestChunks){
    const pines=new T.InstancedMesh(foliageGeo,foliageMat,grove.length),trunks=new T.InstancedMesh(trunkGeo,material('#6e7064'),grove.length);
    pines.name=`forest-${chunk}`;trunks.name=`trunks-${chunk}`;
    pines.castShadow=true;pines.receiveShadow=true;trunks.castShadow=true;
    for(let i=0;i<grove.length;i++){
      const p=grove[i];dummy.position.set(p.x,alpineGround(p.x,p.z),p.z);dummy.rotation.set(0,p.rotation,0);dummy.scale.setScalar(p.scale);dummy.updateMatrix();pines.setMatrixAt(i,dummy.matrix);
      dummy.position.y+=p.scale*.9;dummy.updateMatrix();trunks.setMatrixAt(i,dummy.matrix);
    }
    pines.computeBoundingSphere();trunks.computeBoundingSphere();root.add(pines,trunks);
  }

  const rockGeo=new T.DodecahedronGeometry(1,0),rockMat=material('#829da7',{flatShading:true}),capMat=material('#e7efea',{flatShading:true});
  for(let i=0;i<220;i++){
    const near=i<160,station=near?48+i*(finishZ-100)/160+rng()*10:45+rng()*(finishZ-60),route=i%3?'valley':'pines',p=routeCenter(route,station),side=near?-1:i%3?1:-1;
    const q=roadCrossSection(route,station,side*(p.halfWidth+(near?3.8+rng()*2.5:6+rng()*17))),{x,z}=q;
    const size=near?.90+rng()*.55:.75+rng()*1.6,length=near?1.5+rng()*.45:.8+rng()*.5,radius=size*Math.max(1,length)*1.05;
    if(corridorAt(x,z).outside<radius+.85)continue;
    const rock=new T.Mesh(rockGeo,rockMat);rock.scale.set(size,size*.7,size*length);rock.position.set(x,alpineGround(x,z)-.16,z);rock.rotation.y=near?p.heading:rng()*6;rock.castShadow=true;rock.receiveShadow=true;root.add(rock);
    const cap=new T.Mesh(rockGeo,capMat);cap.position.copy(rock.position);cap.position.y+=size*.44;cap.scale.set(size*.94,size*.34,size*length*.94);cap.rotation.copy(rock.rotation);cap.receiveShadow=true;root.add(cap);obstacles.push({type:'rock',x,z,radius,roadside:near});
  }

  // A few embedded rock ribs identify the uphill cut. Their snow caps and
  // the lower valley shoulder describe a mountain road without railings.
  for(const [route,station] of [...waves.flatMap(([a,b])=>[.17,.46,.79].map(t=>['valley',a+(b-a)*t])),['valley',3030],['pines',3150],['pines',3350]]){
    const p=routeCenter(route,station),q=roadCrossSection(route,station,-p.halfWidth-7.5);
    if(corridorAt(q.x,q.z).outside<6.8)continue;
    const rock=new T.Mesh(rockGeo,rockMat),width=2.8+rng()*1.1,height=2.2+rng()*1.4,length=4.8+rng()*1.5;
    rock.scale.set(width,height,length);rock.position.set(q.x,q.y-.6,q.z);rock.rotation.set(.08,p.heading-.12,0);rock.castShadow=true;rock.receiveShadow=true;root.add(rock);
    const cap=new T.Mesh(rockGeo,capMat);cap.position.copy(rock.position);cap.position.y+=height*.65;cap.scale.set(width*1.03,height*.36,length*.97);cap.rotation.copy(rock.rotation);cap.receiveShadow=true;root.add(cap);
    obstacles.push({type:'rock',x:q.x,z:q.z,radius:Math.max(width,length)*.76,roadside:true});
  }

  function mountainRange(distance,baseHeight,rotation,seed,heightScale=1,colorway=0,originZ=0){
    const random=randomSeed(seed),peaks=Array.from({length:11},(_,i)=>({x:-720+i*144+(random()-.5)*70,height:95+random()*170,width:50+random()*85}));
    const ps=[],cs=[],ids=[],columns=120,rows=27;
    const rock=new T.Color(['#63869f','#7198a2','#91a8bf'][colorway]),cap=new T.Color(['#d4e5ec','#d8eae8','#dce6ef'][colorway]);
    for(let j=0;j<=rows;j++)for(let i=0;i<=columns;i++){
      const x=-900+i*1800/columns,t=j/rows;
      let ridge=12;for(const peak of peaks)ridge+=peak.height*Math.exp(-(((x-peak.x)/peak.width)**2));
      const sharp=Math.sin(t*Math.PI)**.84;
      const z=distance+t*220,base=rotation?downhillHeight(originZ-Math.sin(rotation)*x+Math.cos(rotation)*z)+baseHeight:baseHeight;
      const y=base+(ridge*sharp+Math.sin(x*.048+t*19)*9*sharp)*heightScale;
      ps.push(x,y,z);
      const snow=clamp(((y-base)/heightScale-45)/130+(Math.sin(x*.024)+Math.sin(t*23))*.12,0,1);
      const c=rock.clone().lerp(cap,snow).multiplyScalar(.88+.12*Math.sin(x*.021+t*16));cs.push(c.r,c.g,c.b);
      if(i<columns&&j<rows){const a=j*(columns+1)+i;ids.push(a,a+columns+1,a+1,a+1,a+columns+1,a+columns+2);}
    }
    const g=new T.BufferGeometry();g.setAttribute('position',new T.Float32BufferAttribute(ps,3));g.setAttribute('color',new T.Float32BufferAttribute(cs,3));g.setIndex(ids);g.computeVertexNormals();
    const mountainMat=new T.MeshBasicMaterial({vertexColors:true,side:T.DoubleSide,toneMapped:false});mountainMaterials.push(mountainMat);
    const mesh=new T.Mesh(g,mountainMat);mesh.rotation.y=rotation;mesh.position.z=originZ;mesh.userData.distant=true;root.add(mesh);return mesh;
  }
  mountainRange(finishZ+800,downhillHeight(finishZ)-35,0,498,.90,2);
  mountainRange(finishZ+420,downhillHeight(finishZ)-25,0,312,.73,0);
  for(let z=350;z<finishZ+400;z+=1050){mountainRange(470,20,Math.PI/2,291+z,.55,1,z);mountainRange(525,20,-Math.PI/2,559+z,.65,0,z);}
  mountainRange(420,20,Math.PI,144,.60,2);

  function label(parent,text,position,width=2.8,height=.65,background='#274e58',foreground='#f4f3df'){
    const canvas=document.createElement('canvas');canvas.width=768;canvas.height=192;
    const ctx=canvas.getContext('2d');ctx.fillStyle=background;ctx.fillRect(0,0,canvas.width,canvas.height);ctx.fillStyle=foreground;ctx.textAlign='center';ctx.textBaseline='middle';ctx.font='600 72px system-ui, sans-serif';ctx.fillText(text,384,98,700);
    const texture=new T.CanvasTexture(canvas);texture.colorSpace=T.SRGBColorSpace;textures.push(texture);
    const signMat=new T.MeshBasicMaterial({map:texture,side:T.DoubleSide,toneMapped:false});signMaterials.push(signMat);
    const face=new T.Mesh(new T.PlaneGeometry(width,height),signMat);face.position.set(...position);face.rotation.y=Math.PI;parent.add(face);return face;
  }

  function cabin(){
    const cabin=new T.Group();cabin.position.set(16,alpineGround(16,23)+.6,23);cabin.rotation.y=Math.PI;root.add(cabin);
    box(cabin,[9,.44,8.4],[0,.13,0],'#80919a');
    box(cabin,[8,3.2,6.5],[0,1.95,0],'#9b7357');
    for(let side of [-1,1])for(let n=0;n<13;n++)box(cabin,[.12,.17,6.58],[side*4.03,.54+n*.24,0],n%3?'#ad8663':'#82664f');
    for(let n=0;n<13;n++)box(cabin,[8.1,.17,.13],[0,.54+n*.24,-3.3],n%3?'#ad8663':'#82664f');
    for(const x of [-3.9,3.9])for(const z of [-3.24,3.24])box(cabin,[.30,3.7,.30],[x,2.01,z],'#654f42');
    box(cabin,[1.25,2.3,.15],[0,1.55,3.31],'#426c6b');box(cabin,[.94,.8,.04],[0,1.97,3.405],'#d9dcbb',{emissive:'#8a6634',emissiveIntensity:.22});box(cabin,[.07,.07,.1],[.39,1.27,3.44],'#e1bd72');
    for(const x of [-2.5,2.5]){
      box(cabin,[1.6,1.28,.17],[x,2.02,3.31],'#614b3f');box(cabin,[1.32,1.0,.035],[x,2.02,3.413],'#eedca1',{emissive:'#b98a4c',emissiveIntensity:.35});box(cabin,[.10,1.07,.05],[x,2.02,3.443],'#836954');box(cabin,[1.4,.09,.05],[x,2.02,3.444],'#836954');box(cabin,[1.83,.16,.43],[x,1.33,3.42],'#e4e9df');
    }
    const gable=new T.Shape();gable.moveTo(-4.4,0);gable.lineTo(0,2.23);gable.lineTo(4.4,0);gable.closePath();
    const attic=new T.Mesh(new T.ExtrudeGeometry(gable,{depth:7.28,bevelEnabled:false}),material('#806149'));attic.position.set(0,3.52,-3.64);cabin.add(attic);
    for(const side of [-1,1]){
      const roof=box(cabin,[5.33,.28,8.16],[side*2.34,4.72,0],'#527779');roof.rotation.z=-side*.47;
      const snow=box(cabin,[5.40,.32,8.33],[side*2.34,4.98,0],'#ecf2e8');snow.rotation.z=-side*.47;
    }
    box(cabin,[.8,2.1,.8],[2,5.4,-1.5],'#96948a');box(cabin,[1.07,.2,1.04],[2,6.48,-1.5],'#eaf0e7');
    box(cabin,[9,.24,2.4],[0,.49,4.35],'#b09776');
    for(const side of [-1,1]){
      for(const z of [3.7,5.15])cylinder(cabin,.09,1.4,[side*4.15,1.26,z],'#967658');
      beam(cabin,[side*4.15,1.7,3.55],[side*4.15,1.7,5.3],.07,'#b18e68');
    }
    // A short set of steps and a rack make this a place to depart from.
    for(let i=0;i<3;i++)box(cabin,[2.4,.15,1.2-i*.23],[0,.07+i*.14,5.42+i*.16],'#bdab8c');
    beam(cabin,[-3.0,.7,4.5],[-3.0,2.2,4.5],.06,'#50797b');beam(cabin,[-1.7,.7,4.5],[-1.7,2.2,4.5],.06,'#50797b');beam(cabin,[-3.0,1.9,4.5],[-1.7,1.9,4.5],.07,'#50797b');
    const board=box(cabin,[.42,1.83,.13],[-2.5,1.34,4.55],'#ea8770');board.rotation.z=-.12;
    const sign=label(cabin,'snowcat',[0,3.01,3.44],3.5,.52,'#315a60');sign.rotation.y=0;
    obstacles.push({type:'cabin',x:16,z:23,radius:5.8});
  }
  cabin();

  function gate(index,p){
    const g=new T.Group();root.add(g);const cloth=material('#ea886a',{side:T.DoubleSide});
    const flags=[];
    for(const side of [-1,1]){
      const x=p.x+side*p.halfWidth,y=alpineGround(x,p.z);
      cylinder(g,.055,2.4,[x,y+1.2,p.z],'#628080');
      const flag=new T.Mesh(new T.PlaneGeometry(1.05,.59),cloth);flag.position.set(x-side*.49,y+1.96,p.z);g.add(flag);flags.push(flag);
      box(g,[.40,.12,.40],[x,y+.045,p.z],'#e6ede6');
    }
    const labelParent=new T.Group();labelParent.position.set(p.x-p.halfWidth,alpineGround(p.x-p.halfWidth,p.z)+.35,p.z-.08);g.add(labelParent);label(labelParent,String(index+1),[0,1.52,-.07],.53,.47,'#e79273');
    gateObjects.push({flags,cloth:cloth.clone()});
    const record=gateObjects.at(-1);for(const flag of flags)flag.material=record.cloth;
  }
  ALPINE_REGION.challenge.gates.forEach((p,i)=>gate(i,p));
  const finishZone=ALPINE_REGION.challenge.exit,zonePositions=[],zoneIndices=[];
  const columns=16,rows=Math.ceil(finishZone.length);
  for(let j=0;j<=rows;j++)for(let i=0;i<=columns;i++){
    const x=finishZone.x-finishZone.halfWidth+i/columns*finishZone.halfWidth*2,z=finishZone.z+j/rows*finishZone.length;
    zonePositions.push(x,alpineGround(x,z)+.025,z);
    if(i<columns&&j<rows){const a=j*(columns+1)+i;zoneIndices.push(a,a+columns+1,a+1,a+1,a+columns+1,a+columns+2);}
  }
  const zoneGeometry=new T.BufferGeometry();zoneGeometry.setAttribute('position',new T.Float32BufferAttribute(zonePositions,3));zoneGeometry.setIndex(zoneIndices);
  const zone=new T.Mesh(zoneGeometry,new T.MeshBasicMaterial({color:'#8fbfa7',transparent:true,opacity:.30,depthWrite:false,polygonOffset:true,polygonOffsetFactor:-1,polygonOffsetUnits:-1}));
  zone.name='challenge-exit-snow';zone.userData.exitBounds={...finishZone};zone.userData.distant=true;root.add(zone);

  const deckZ=finishZ+13,deck=new T.Group();deck.position.set(0,alpineGround(0,deckZ)-.30,deckZ);root.add(deck);
  box(deck,[22,.42,8],[0,.05,0],'#b0a28a');
  for(let i=0;i<32;i++)box(deck,[.62,.055,7.88],[-10.3+i*.66,.286,0],'#c2b79f');
  for(const x of [-10.8,-5.4,0,5.4,10.8])cylinder(deck,.095,1.5,[x,1.02,3.78],'#9b937e');
  beam(deck,[-11,1.48,3.78],[11,1.48,3.78],.075,'#b4aa91');
  for(const x of [-8.5,8.5]){
    box(deck,[3.4,.19,.73],[x,.85,.6],'#beab88');box(deck,[3.4,.18,.48],[x,1.22,1.09],'#bba482');
    for(const side of [-1,1])box(deck,[.15,.63,.61],[x+side*1.2,.48,.6],'#657c76');
  }

  const skyMat=new T.ShaderMaterial({side:T.BackSide,depthWrite:false,toneMapped:false,uniforms:{top:{value:new T.Color(palettes.day.skyTop)},bottom:{value:new T.Color(palettes.day.skyBottom)}},vertexShader:'varying vec3 vPosition; void main(){vPosition=position;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}',fragmentShader:'uniform vec3 top; uniform vec3 bottom; varying vec3 vPosition; void main(){float h=clamp(normalize(vPosition).y*2.5+.25,0.,1.);gl_FragColor=vec4(mix(bottom,top,pow(h,.65)),1.);\n#include <tonemapping_fragment>\n#include <colorspace_fragment>\n}'});
  const sky=new T.Mesh(new T.SphereGeometry(1850,32,20),skyMat);sky.renderOrder=-10;root.add(sky);
  const starRng=randomSeed(5427),starData=[];for(let i=0;i<170;i++){const angle=starRng()*Math.PI*2,y=.12+starRng()*.83,r=Math.sqrt(1-y*y);starData.push(Math.sin(angle)*r*1700,y*1700,Math.cos(angle)*r*1700);}
  const starGeo=new T.BufferGeometry();starGeo.setAttribute('position',new T.Float32BufferAttribute(starData,3));
  const stars=new T.Points(starGeo,new T.PointsMaterial({color:'#c5dbf3',size:2.3,transparent:true,opacity:.68,depthWrite:false,fog:false,toneMapped:false}));stars.visible=false;sky.add(stars);
  const moon=new T.Mesh(new T.SphereGeometry(18,16,10),new T.MeshBasicMaterial({color:'#c9e2fb',fog:false,toneMapped:false}));moon.position.set(-230,330,1590);moon.visible=false;sky.add(moon);
  const sun=new T.DirectionalLight(palettes.day.sun,palettes.day.sunPower);sun.position.set(...palettes.day.light);sun.castShadow=true;sun.shadow.mapSize.set(2048,2048);Object.assign(sun.shadow.camera,{left:-36,right:36,top:36,bottom:-36,near:1,far:180});sun.shadow.normalBias=.025;sun.shadow.bias=-.00012;
  sun.shadow.camera.updateProjectionMatrix();
  const hemi=new T.HemisphereLight(palettes.day.skyLight,palettes.day.groundLight,palettes.day.ambient);root.add(sun,sun.target,hemi);

  const layers=[];
  const flakeCanvas=document.createElement('canvas');flakeCanvas.width=flakeCanvas.height=32;const fc=flakeCanvas.getContext('2d'),gradient=fc.createRadialGradient(16,16,1,16,16,15);gradient.addColorStop(0,'rgba(255,255,255,.96)');gradient.addColorStop(.45,'rgba(255,255,255,.72)');gradient.addColorStop(1,'rgba(255,255,255,0)');fc.fillStyle=gradient;fc.fillRect(0,0,32,32);const flakeTexture=new T.CanvasTexture(flakeCanvas);textures.push(flakeTexture);
  for(const [count,span,size,speed] of [[360,44,.16,1.8],[400,95,.20,1.2],[310,180,.27,.7]]){
    const data=new Float32Array(count*3),fall=new Float32Array(count);for(let i=0;i<count;i++){data[i*3]=(rng()-.5)*span;data[i*3+1]=rng()*35;data[i*3+2]=(rng()-.5)*span;fall[i]=.8+rng()*.6;}
    const geo=new T.BufferGeometry();geo.setAttribute('position',new T.BufferAttribute(data,3).setUsage(T.DynamicDrawUsage));const points=new T.Points(geo,new T.PointsMaterial({color:'#f8ffff',size,map:flakeTexture,transparent:true,opacity:.68,depthWrite:false,sizeAttenuation:true}));points.visible=false;points.frustumCulled=false;root.add(points);layers.push({points,data,fall,span,speed,initialized:false});
  }
  let weather='clear',timeOfDay='day',weatherMix=0,snowTime=0;
  const followPosition=new T.Vector3(),lightOffset=new T.Vector3(-34,54,-28);
  function applyLighting(){
    const snowy=weather==='snow',base=palettes[timeOfDay],p={...base,...(snowy?cloudPalettes[timeOfDay]:{})};
    scene.fog=new T.Fog(p.fog,p.fogNear,p.fogFar);skyMat.uniforms.top.value.set(p.skyTop);skyMat.uniforms.bottom.value.set(p.skyBottom);sun.color.set(p.sun);sun.intensity=p.sunPower*(snowy?.43:1);hemi.color.set(p.skyLight);hemi.groundColor.set(p.groundLight);hemi.intensity=p.ambient;lightOffset.set(...p.light);
    sun.target.position.copy(followPosition);sun.position.copy(followPosition).add(lightOffset);
    moon.position.copy(lightOffset).normalize().multiplyScalar(1700);
    for(const mat of mountainMaterials)mat.color.set(p.mountain);
    for(const mat of signMaterials)mat.color.set(p.sign);
    for(const mat of materials.values())if(mat.emissive?.getHex())mat.emissiveIntensity=timeOfDay==='night'?1.05:timeOfDay==='sunset'?.50:.16;
    moon.visible=timeOfDay==='night'&&!snowy;stars.visible=timeOfDay==='night'&&!snowy;
    for(const layer of layers){layer.points.visible=snowy;layer.points.material.color.set(p.flakes);layer.points.material.opacity=timeOfDay==='night'?.68:.82;}
    return {...p,sunPower:sun.intensity,weather,timeOfDay};
  }
  function setWeather(next){
    weather=next==='snow'?'snow':'clear';weatherMix=weather==='snow'?1:0;return applyLighting();
  }
  function setTimeOfDay(next){
    timeOfDay=['day','sunset','night'].includes(next)?next:'day';return applyLighting();
  }
  function follow(player){
    const p=player.position||player;followPosition.set(p.x||0,Number.isFinite(p.y)?p.y:alpineGround(p.x||0,p.z||0),p.z||0);sun.target.position.copy(followPosition);sun.position.copy(followPosition).add(lightOffset);sky.position.copy(followPosition);
  }
  function update(dt,player,camera){
    follow(player);if(weather!=='snow')return;snowTime+=dt;weatherMix=Math.min(1,weatherMix+dt);
    const cp=camera?.position||followPosition;
    for(const layer of layers){
      const {data,span,speed,fall}=layer;
      if(!layer.initialized){for(let i=0;i<fall.length;i++){data[i*3]+=cp.x;data[i*3+1]+=cp.y-8;data[i*3+2]+=cp.z;}layer.initialized=true;}
      for(let i=0;i<fall.length;i++){
        const j=i*3;data[j]+=dt*(.65+Math.sin(snowTime*.35+i)*.22);data[j+1]-=dt*speed*fall[i];data[j+2]-=dt*.22;
        // Keep flakes in world space, wrapping only outside the viewing volume;
        // skiing through them then produces real foreground/background parallax.
        data[j]=cp.x+((data[j]-cp.x+span*.5)%span+span)%span-span*.5;
        data[j+1]=cp.y-8+((data[j+1]-cp.y+8)%35+35)%35;
        data[j+2]=cp.z+((data[j+2]-cp.z+span*.5)%span+span)%span-span*.5;
      }
      layer.points.material.opacity=(timeOfDay==='night'?.68:.82)*weatherMix;layer.points.geometry.attributes.position.needsUpdate=true;
    }
  }
  function setChallenge(state={}){
    const passed=state.gatesPassed??state.passed??state.gateIndex??state.gates??0,active=state.status==='active'||state.challenge==='active'||state.active===true;
    for(let i=0;i<gateObjects.length;i++)gateObjects[i].cloth.color.set(i<passed?'#8cc8ad':active&&i===passed?'#f2bb65':'#ea886a');
  }
  // Bake nearby woodwork, stones and signs together, never the whole mountain.
  // Spatial bounds allow the renderer to cull scenery outside the viewing cone.
  root.updateWorldMatrix(true,true);
  const batches=new Map(),retired=new Set();
  root.traverse(object=>{
    if(!object.isMesh||object.isInstancedMesh||object.material.isShaderMaterial||object.userData.terrain||object.userData.distant)return;
    if(!object.geometry.boundingSphere)object.geometry.computeBoundingSphere();
    const center=object.geometry.boundingSphere.center.clone().applyMatrix4(object.matrixWorld),chunk=Math.floor(center.z/140);
    const key=chunk+'/'+object.material.uuid+'/'+object.castShadow+'/'+object.receiveShadow+'/'+Object.keys(object.geometry.attributes).sort().join(',');
    if(!batches.has(key))batches.set(key,[]);batches.get(key).push(object);
  });
  for(const meshes of batches.values()){
    if(meshes.length<2)continue;
    const parts=meshes.map(mesh=>{const geometry=mesh.geometry.index?mesh.geometry.toNonIndexed():mesh.geometry.clone();return geometry.applyMatrix4(mesh.matrixWorld);});
    const merged=mergeGeometries(parts,false);for(const part of parts)part.dispose();if(!merged)continue;
    merged.computeBoundingSphere();const mesh=new T.Mesh(merged,meshes[0].material);mesh.name='scenery-section';mesh.castShadow=meshes[0].castShadow;mesh.receiveShadow=meshes[0].receiveShadow;root.add(mesh);
    for(const old of meshes){retired.add(old.geometry);old.removeFromParent();}
  }
  const stillUsed=new Set();root.traverse(object=>{if(object.geometry)stillUsed.add(object.geometry);});for(const geometry of retired)if(!stillUsed.has(geometry))geometry.dispose();
  setWeather('clear');
  return {root,groundMat,sun,hemi,obstacles,update,follow,setWeather,setTimeOfDay,setChallenge,
    get weather(){return weather;},
    get timeOfDay(){return timeOfDay;},
    dispose(){root.removeFromParent();scene.fog=previousFog;const geometries=new Set(),mats=new Set();root.traverse(obj=>{if(obj.geometry)geometries.add(obj.geometry);if(obj.material)for(const m of Array.isArray(obj.material)?obj.material:[obj.material])mats.add(m);});for(const g of geometries)g.dispose();for(const m of mats)m.dispose();for(const tex of textures)tex.dispose();sun.shadow.map?.dispose();}
  };
}
